/**
 * MM-FIX-E (2026-04-19) — Histórico de mídia recebida
 * Mostra as descrições/transcrições de imagens, áudios, vídeos, figurinhas
 * e documentos que a Syra processou nos grupos da organização.
 *
 * Substitui o antigo painel "JarvisObservabilityTab" para inspeção de mídia,
 * que lia da tabela `jarvis_multimodal_log` (zero registros). Esta página lê
 * de `multimodal_descriptions` (a tabela viva alimentada pelo webhook).
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Image as ImageIcon, Mic, Video, Sticker, FileText, Link as LinkIcon, RefreshCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

type MediaRow = {
  id: string;
  org_id: string;
  group_id: string | null;
  message_id: string | null;
  sender_name: string | null;
  kind: "image" | "audio" | "video" | "sticker" | "document" | "link";
  mimetype: string | null;
  description: string;
  transcript: string | null;
  caption: string | null;
  model_used: string | null;
  confidence: number | null;
  source: string;
  created_at: string;
};

const KIND_META: Record<MediaRow["kind"], { label: string; icon: typeof ImageIcon; tone: string }> = {
  image: { label: "Imagem", icon: ImageIcon, tone: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" },
  audio: { label: "Áudio", icon: Mic, tone: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30" },
  video: { label: "Vídeo", icon: Video, tone: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30" },
  sticker: { label: "Figurinha", icon: Sticker, tone: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  document: { label: "Documento", icon: FileText, tone: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30" },
  link: { label: "Link", icon: LinkIcon, tone: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30" },
};

export default function MediaHistory() {
  const [rows, setRows] = useState<MediaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<MediaRow["kind"] | "all">("all");
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("multimodal_descriptions")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (!error && data) setRows(data as MediaRow[]);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filter !== "all" && r.kind !== filter) return false;
      if (query) {
        const q = query.toLowerCase();
        return (
          r.description.toLowerCase().includes(q) ||
          (r.transcript ?? "").toLowerCase().includes(q) ||
          (r.caption ?? "").toLowerCase().includes(q) ||
          (r.sender_name ?? "").toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [rows, filter, query]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length };
    rows.forEach((r) => { c[r.kind] = (c[r.kind] ?? 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div className="container max-w-6xl py-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm">
            <Link to="/dashboard"><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Link>
          </Button>
          <div>
            <h1 className="text-2xl font-headline font-semibold">Mídia recebida</h1>
            <p className="text-sm text-muted-foreground">
              Descrições e transcrições de imagens, áudios, vídeos, figurinhas e documentos analisados pela Syra.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-2">
            {(["all", "image", "audio", "video", "sticker", "document", "link"] as const).map((k) => (
              <Button
                key={k}
                size="sm"
                variant={filter === k ? "default" : "outline"}
                onClick={() => setFilter(k)}
                className="h-8"
              >
                {k === "all" ? "Todos" : KIND_META[k as MediaRow["kind"]].label}
                <Badge variant="secondary" className="ml-2 px-1.5 py-0 text-[10px]">
                  {counts[k] ?? 0}
                </Badge>
              </Button>
            ))}
            <Input
              placeholder="Buscar na descrição, transcrição, autor..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="ml-auto max-w-xs h-8"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Carregando…</CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>Nenhuma mídia encontrada com esses filtros.</p>
            <p className="text-xs mt-1">Mande uma foto, áudio ou figurinha em um grupo conectado para ver aparecer aqui.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const meta = KIND_META[r.kind];
            const Icon = meta.icon;
            return (
              <Card key={r.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={meta.tone}>
                        <Icon className="w-3 h-3 mr-1" /> {meta.label}
                      </Badge>
                      {r.sender_name && (
                        <span className="text-sm font-medium">{r.sender_name}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {r.model_used && <span className="font-mono">{r.model_used}</span>}
                      {typeof r.confidence === "number" && (
                        <Badge variant="outline" className="text-[10px]">
                          conf {r.confidence.toFixed(2)}
                        </Badge>
                      )}
                      <span>{formatDistanceToNow(new Date(r.created_at), { addSuffix: true, locale: ptBR })}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {r.caption && (
                    <p className="text-sm italic text-muted-foreground">Legenda: "{r.caption}"</p>
                  )}
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{r.description}</p>
                  {r.transcript && (
                    <div className="rounded-md bg-muted/50 p-2 border border-border/50">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Transcrição</p>
                      <p className="text-sm whitespace-pre-wrap">{r.transcript}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
